import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';

import { site } from '../../configs/site';
import SiteFooter from '../../components/SiteFooter/SiteFooter';
import {
  preloadHubSpotFormsScript,
  HUBSPOT_FORM_ID,
  HUBSPOT_PORTAL_ID,
  HUBSPOT_REGION,
} from '../../services/HubSpotForms';

// Same 5-stop gradient used on the use-case one-pagers, as a thin accent strip.
const familyGradient = (deg = 120) =>
  `linear-gradient(${deg}deg, #60a5fa 0%, #1d4ed8 38%, #4f46e5 62%, #6d28d9 82%, #9d174d 100%)`;

const ContactView = () => {
  const formContainerRef = useRef(null);
  const location = useLocation();

  // ContactCTA (shown at the bottom of each use case page) passes the use
  // case's own path along as router state when it sends a visitor here, so
  // the back link below can return them to that same use case instead of
  // always landing on the homepage. Arriving here any other way (the top
  // nav's "Contact us" button, a direct link, etc.) has no such state, so
  // this falls back to "/" -- same as before.
  const backTo = location.state?.from || '/';
  const backLabel = location.state?.from ? 'Back to use case' : 'Back to home';

  useEffect(() => {
    let isCancelled = false;

    preloadHubSpotFormsScript().then((hbspt) => {
      if (isCancelled || !hbspt?.forms || !formContainerRef.current) {
        return;
      }
      formContainerRef.current.innerHTML = '';
      hbspt.forms.create({
        region: HUBSPOT_REGION,
        portalId: HUBSPOT_PORTAL_ID,
        formId: HUBSPOT_FORM_ID,
        target: `#${formContainerRef.current.id}`,
      });
    });

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-1 flex-col">
      {/* Cancels the negative margin below so the gradient bleeds edge-to-edge without causing horizontal scroll. */}
      <div className="px-4 sm:px-6 lg:px-10">
      {/* Full-bleed gradient wash behind the page. */}
      <div className="relative isolate -mx-4 overflow-hidden bg-gradient-to-b from-[#eef0fb] via-[#f4eef7] to-white px-4 pb-14 pt-6 sm:-mx-6 sm:px-8 sm:pb-20 lg:-mx-10 lg:px-14">
        <Link
          to={backTo}
          className="relative mb-8 flex w-fit items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <span aria-hidden="true">&larr;</span>
          {backLabel}
        </Link>

        <div className="relative mx-auto grid max-w-5xl grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-start lg:gap-16">
          {/* Left column: page framing/copy, separate from the form's own heading. */}
          <div className="lg:pt-4">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700">
              <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Get in touch
            </span>

            <h1 className="mt-4 text-3xl font-bold text-gray-900 sm:text-4xl" style={{ textWrap: 'balance' }}>
              {site.contact.title}
            </h1>

            <p className="mt-4 max-w-md text-base text-gray-600 sm:text-lg">
              {site.contact.description}
            </p>
          </div>

          {/* Right column: the form, framed as a card. */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
            <div className="h-1.5 w-full" style={{ background: familyGradient(100) }} aria-hidden="true" />

            <div className="p-6 sm:p-8">
              {/* Plain, unmarked container -- filled exclusively by the
                  explicit hbspt.forms.create() call in the effect above.
                  See that effect's comment for why this deliberately does
                  NOT also carry HubSpot's class="hs-form-frame" +
                  data-portal-id/data-form-id/data-region auto-embed
                  markers. */}
              <div ref={formContainerRef} id="hs-contact-form" />
            </div>
          </div>
        </div>
      </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default ContactView;
